import { LoadingOutlined } from '@ant-design/icons'
import Api from '@vacationtracker/shared/services/api'
import { signout } from '@vacationtracker/shared/services/auth'
import { Alert, App, Button, Col, Input, Modal, Radio, Row, Select, Space, Table, Typography, Tooltip, Card, Progress } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { RadioChangeEvent } from 'antd/lib/radio'
import { useManualQuery } from 'graphql-hooks'
import capitalize from 'lodash/capitalize'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Redirect, useHistory, useLocation } from 'react-router'
import { notificationStore } from '../../context/notificationsContext/store'
import { getAllUserListForMigrationPaginated } from '../../graphql/custom-queries'
import { MicrosoftAuth } from '../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../services/auth/slack/slack'
import { GoogleAuth } from '../../services/auth/google/google'
import * as logger from '../../services/logger'
import { logoutAction } from '../../store/configure'
import { useAppDispatch } from '../../store/hooks'
import { FrontendUrls } from '../../types/urls'
import { IGetAllUserListForMigrationPaginatedData, UserStatus } from '../../types/users'
import IntlMessages from '../../util/IntlMessages'
import { validEmailWithRegex } from '@vacationtracker/shared/functions/valid-email-with-regex'
import { ICompanyPlatformChangedEventBody } from './types'
import { 
  fetchMicrosoftUsers,
  fetchSlackUsers,
  fetchGoogleUsers,
  getTokensForPlatform, 
  isGoogleUser, 
  isMicrosoftUser, 
  isSlackUser, 
  normalizeFullName,
  saveUserMappingsToStorage,
  Platform,
  PlatformUser,
  UserMappings,
  IUserMapping,
  LocationState,
  getOrganizationId
} from './util'
import useDebounce from '../../util/use-debounce'
import { track } from '../../services/analytics/analytics'
const { Title, Text } = Typography

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID as string)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID as string)

const MAX_USERS_FOR_MIGRATION = 500
interface IGetUserListForMigration {
  id: string
  name: string
  status: UserStatus
  email: string
  isAdmin: boolean
  initialUser: boolean
}

const MigrationPage: React.FC = () => {
  const { state } = useLocation<LocationState>()
  const history = useHistory()
  const reduxDispatch = useAppDispatch()
  const { modal, notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [correlationId, setCorrelationId] = useState<string | null>()
  const { formatMessage } = useIntl()
  const [searchText, setSearchText] = useState('')
  const [markUnmappedAsDeleted, setMarkUnmappedAsDeleted] = useState(false)
  const [usersForMigration, setUsersForMigration] = useState<IGetUserListForMigration[]>([])
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingMapping, setIsSavingMapping] = useState(false)
  const [userMappings, setUserMappings] = useState<UserMappings>({})
  const [activeSelectId, setActiveSelectId] = useState<string | null>(null)
  const [invalidEmails, setInvalidEmails] = useState<Record<string, boolean>>({})
  const [emailInputs, setEmailInputs] = useState<Record<string, string>>({})
  const [duplicateEmails, setDuplicateEmails] = useState<Record<string, boolean>>({})
  const [duplicateMappings, setDuplicateMappings] = useState<Record<string, { duplicateUserEmail: string; displayName: string }>>({})
  const [hideInactive, setHideInactive] = useState(false)
  const [searchPlatformUsersText, setSearchPlatformUsersText] = useState('')
  const [usersForMigrationLoading, setUsersForMigrationLoading] = useState(false)
  const [unmappedUsers, setUnmappedUsers] = useState(0)
  const [isAboveMaxUsers, setIsAboveMaxUsers] = useState(false)
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false)
  const [countdown, setCountdown] = useState(15)
  const [isFetchingUsers, setIsFetchingUsers] = useState(false)
  const [isFetchingPlatformUsers, setIsFetchingPlatformUsers] = useState(false)
  const [isMappingUsers, setIsMappingUsers] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [loadingMessage, setLoadingMessage] = useState('Initializing...')
  const [isFilteringPlatformUsers, setIsFilteringPlatformUsers] = useState(false)
  const [getAllUserListForMigrationPaginatedQuery] = useManualQuery<IGetAllUserListForMigrationPaginatedData, {
    limit: number
    nextToken?: string
  }>(getAllUserListForMigrationPaginated)
  
  const { 
    newPlatform, 
    oldPlatform, 
    tenantId, 
    slackOrgTeamId, 
    slackBotId, 
    organizationId,
    googleDomain,
  } = state as { 
    newPlatform: Platform 
    oldPlatform: Platform 
    tenantId: string
    slackOrgTeamId: string
    slackBotId: string
    organizationId: string
    googleDomain: string
  }
  
  // Add debounced inputs for validation
  const debouncedEmailInputs = useDebounce(emailInputs, 300) as Record<string, string>
  
  // Use effect to validate debounced email inputs
  useEffect(() => {
    // Validate each email
    Object.entries(debouncedEmailInputs).forEach(([userId, email]) => {
      validateEmailFormat(email, userId)
    })
  }, [debouncedEmailInputs])
  
  // Add useEffect for email validation
  useEffect(() => {
    if (Object.keys(debouncedEmailInputs).length > 0) {
      // Check each email for validity and duplicates
      validateEmailsForDuplicates()
    }
  }, [debouncedEmailInputs])

  useEffect(() => {
    if (correlationId && !actionNotifications.includes(correlationId) && isSavingMapping) {
      setIsSavingMapping(false)
      setCorrelationId(null)
      setIsLogoutModalVisible(true)
    }
  }, [actionNotifications])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isLogoutModalVisible && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    } else if (countdown === 0) {
      handleLogout()
    }
    return () => clearTimeout(timer)
  }, [isLogoutModalVisible, countdown])

  const handleLogout = async () => {
    await signout(true)
    reduxDispatch(logoutAction)
    history.push(FrontendUrls.signin)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setLoadingMessage('Initializing...')
      setLoadingProgress(10)
      
      try {
        // Fetch users in system
        setIsFetchingUsers(true)
        setLoadingMessage('Fetching users from database...')
        setLoadingProgress(20)
        await fetchAllUsersInSystem()
        setIsFetchingUsers(false)
        setLoadingProgress(40)
        
        // Fetch platform users
        setIsFetchingPlatformUsers(true)
        setLoadingMessage(`Fetching users from ${capitalize(newPlatform)}...`)
        setLoadingProgress(60)
        
        try {
          // Set a timeout to ensure progress continues even if fetching takes too long
          const platformFetchTimeout = setTimeout(() => {
            // If it's taking too long, we still want to show progress
            if (newPlatform !== 'email') {
              setLoadingProgress(70)
              setLoadingMessage(`Still fetching users from ${capitalize(newPlatform)}...`)
            }
          }, 5000)
          
          // For email platform, we don't need to fetch external users
          if (newPlatform === 'email') {
            // No need to fetch anything for email platform
            setPlatformUsers([])
          } else {
            await fetchUsersFromNewPlatform()
          }
          
          clearTimeout(platformFetchTimeout)
        } catch (error) {
          logger.error(`Error fetching ${newPlatform} users:`, error)
          notification.warning({
            message: `Issue fetching ${capitalize(newPlatform)} users`,
            description: 'We encountered an issue but will continue with any users we could retrieve.',
          })
          
          // If platform fetch fails, we still want to continue with any users we got
          if (platformUsers.length === 0 && newPlatform !== 'email') {
            setPlatformUsers([])
          }
        }
        
        setIsFetchingPlatformUsers(false)
        setLoadingProgress(80)
        
        // Mapping is handled in useEffect when platformUsers and usersForMigration change
        setIsMappingUsers(true)
        setLoadingMessage('Mapping users...')
        
        // Ensure mapping progress continues even if there are issues with the useEffect
        setTimeout(() => {
          if (loadingProgress === 80) {
            setLoadingProgress(90)
            
            // If we're still stuck, force complete after 10 seconds
            setTimeout(() => {
              if (isLoading) {
                setIsMappingUsers(false)
                setIsLoading(false)
                setLoadingProgress(100)
              }
            }, 10000)
          }
        }, 5000)
        
      } catch (error) {
        logger.error('Error fetching data:', error)
        notification.error({
          message: 'Error loading data',
          description: 'There was a problem fetching user data. Please try again.',
        })
        
        // Force exit loading state after error
        setIsFetchingUsers(false)
        setIsFetchingPlatformUsers(false)
        setIsMappingUsers(false)
        setIsLoading(false)
      }
    }

    fetchData()
  }, [newPlatform])

  // This effect runs when both platformUsers and usersForMigration are loaded
  useEffect(() => {
    if ((platformUsers.length > 0 || newPlatform === 'email') && usersForMigration.length > 0) {
      setIsMappingUsers(true)
      setLoadingMessage('Mapping users...')
      setLoadingProgress(90)
      
      try {
        // Create mappings (existing code)
        const newMappings: Record<
        string,
        {
          id: string
          displayName: string
          mail: string
          isOwner?: boolean
          isAdmin?: boolean
          initialUser?: boolean
        } | null
        > = {}
        
        usersForMigration.forEach(user => {
          // For email platform, use existing email
          if (newPlatform === 'email') {
            newMappings[user.id] = {
              id: user.id,
              displayName: user.name,
              mail: user.email,
              isAdmin: user.isAdmin,
              initialUser: user.initialUser,
            }
            return
          }
          
          const matchedUser = platformUsers.find(platformUser => {
            // Existing matching logic
            if (isMicrosoftUser(platformUser)) {
              return platformUser.mail?.toLowerCase() === user.email?.toLowerCase() ||
                platformUser.userPrincipalName?.toLowerCase() === user.email?.toLowerCase() ||
                normalizeFullName(platformUser.displayName || '') === normalizeFullName(user.name || '')
            } else if (isSlackUser(platformUser)) {
              return platformUser.profile.email?.toLowerCase() === user.email?.toLowerCase() ||
                normalizeFullName(platformUser.profile.real_name_normalized || '') === normalizeFullName(user.name || '')
            } else if (isGoogleUser(platformUser)) {
              return platformUser.email?.toLowerCase() === user.email?.toLowerCase() ||
                normalizeFullName(platformUser.name || '') === normalizeFullName(user.name || '')
            }
            return false
          })
          
          if (matchedUser) {
            newMappings[user.id] = {
              id: matchedUser.id,
              displayName: isMicrosoftUser(matchedUser) 
                ? matchedUser.displayName || matchedUser.id
                : isSlackUser(matchedUser)
                  ? matchedUser.profile.real_name_normalized || matchedUser.id
                  : matchedUser.name || matchedUser.id,
              mail: isMicrosoftUser(matchedUser) 
                ? matchedUser.mail 
                : isSlackUser(matchedUser)
                  ? matchedUser.profile.email
                  : matchedUser.email,
              isOwner: isSlackUser(matchedUser) ? matchedUser.is_primary_owner : undefined,
              isAdmin: user.isAdmin,
              initialUser: user.initialUser,
            }
          } else {
            newMappings[user.id] = null
          }
        })
        
        setUserMappings(prevMappings => {
          return {
            ...newMappings,
            ...prevMappings,
          }
        })
        setUnmappedUsers(Object.values({
          ...newMappings,
          ...userMappings,
        }).filter(value => value === null).length)
      } catch (error) {
        logger.error('Error mapping users:', error)
      } finally {
        // Always complete the loading process, even if there are errors
        setTimeout(() => {
          setIsMappingUsers(false)
          setIsLoading(false)
          setLoadingProgress(100)
        }, 500)
      }
    }
  }, [platformUsers, usersForMigration, newPlatform])

  useEffect(() => {
    saveUserMappingsToStorage(userMappings)
  }, [userMappings])

  const filteredPlatformUsers = useMemo(() => {
    // Only set filtering state when actually searching
    if (searchPlatformUsersText && platformUsers.length > 100) {
      setIsFilteringPlatformUsers(true)
    }
    
    const filtered = platformUsers.filter(user => {
      if (isMicrosoftUser(user)) {
        return user.displayName?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase()) ||
          user.mail?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase()) ||
          user.userPrincipalName?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase())
      } else if (isSlackUser(user)) {
        return user.profile.real_name_normalized?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase()) ||
          user.profile.email?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase())
      } else if (isGoogleUser(user)) {
        return user.name?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchPlatformUsersText?.toLowerCase())
      }
      return false
    })
    
    // Clear filtering state once done
    setIsFilteringPlatformUsers(false)
    return filtered
  }, [platformUsers, searchPlatformUsersText])

  // Initialize userMappings for email platform
  useEffect(() => {
    if (newPlatform === 'email' && usersForMigration.length > 0 && Object.keys(userMappings).length === 0) {
      const initialMappings: Record<string, IUserMapping> = {}
      
      // Initialize mappings for all users
      usersForMigration.forEach(user => {
        initialMappings[user.id] = {
          id: user.id,
          displayName: user.name,
          mail: user.email,
          isAdmin: user.isAdmin,
          initialUser: user.initialUser,
        }
      })
      
      setUserMappings(initialMappings)
      saveUserMappingsToStorage(initialMappings)
    }
  }, [newPlatform, usersForMigration, userMappings])

  // Add useEffect to update userMappings when valid email inputs change
  useEffect(() => {
    if (Object.keys(debouncedEmailInputs).length > 0 && newPlatform === 'email') {
      const updatedMappings = { ...userMappings }
      let hasChanges = false
      
      Object.entries(debouncedEmailInputs).forEach(([userId, email]) => {
        // Only update if email is valid
        if (email && !invalidEmails[userId] && !duplicateEmails[userId]) {
          // Get existing mapping or create a new one
          const existingMapping = updatedMappings[userId] || {} as IUserMapping
          const user = usersForMigration.find(u => u.id === userId)
          
          // Only update if the email has actually changed
          if (existingMapping.mail !== email) {
            hasChanges = true
            // Create/update the mapping with the new email
            updatedMappings[userId] = {
              ...existingMapping,
              id: userId,
              displayName: user?.name || existingMapping.displayName || '',
              mail: email,
              isAdmin: user?.isAdmin || existingMapping.isAdmin,
              initialUser: user?.initialUser || existingMapping.initialUser,
            }
          }
        }
      })
      
      // Only update state if there are actual changes
      if (hasChanges) {
        setUserMappings(updatedMappings)
        saveUserMappingsToStorage(updatedMappings)
      }
    }
  }, [debouncedEmailInputs, invalidEmails, duplicateEmails, newPlatform, usersForMigration])

  // this needs to reset every time i open select
  const userOptions = useMemo(() => [
    { value: 'unmapped', label: formatMessage({ id: 'company.platformChanged.unmatched' }, { platform: capitalize(newPlatform) }) },
    ...filteredPlatformUsers.map(user => ({
      value: user.id,
      label: isMicrosoftUser(user) ? user.displayName : isSlackUser(user) ? user.profile.real_name_normalized : user.name,
    })),
  ], [filteredPlatformUsers, formatMessage])

  //prevent landing on this page without state
  if (!state) {
    logger.error('no state redirecting to general')
    return <Redirect to="/app/settings/general" />
  }

  const fetchAllUsersInSystem = async (nextToken?: string, limit = 200, shortUsers: IGetUserListForMigration[] = []): Promise<void> => {
    setUsersForMigrationLoading(true)
    const usersInSystem = await getAllUserListForMigrationPaginatedQuery({ variables: { nextToken, limit } })
    if (!usersInSystem.data || usersInSystem.error) throw usersInSystem.error

    const users: IGetUserListForMigration[] = shortUsers.concat(usersInSystem.data.getAllUserListPaginated.users as IGetUserListForMigration[])

    if (usersInSystem.data.getAllUserListPaginated.nextToken) {
      setLoadingMessage(`Fetching users from database (${users.length} loaded so far)...`)
      return await fetchAllUsersInSystem(usersInSystem.data.getAllUserListPaginated.nextToken, limit, users)
    }

    if (users.length > MAX_USERS_FOR_MIGRATION) {
      setUsersForMigration(users.filter(user => user.status === 'ACTIVE'))
      setIsAboveMaxUsers(true)
    } else {
      setUsersForMigration(users)
    }

    setUsersForMigrationLoading(false)
  }

  const fetchUsersFromNewPlatform = async () => {
    const handleError = (error: Error) => {
      logger.error(`Failed to fetch ${newPlatform} users:`, error)
      history.push('/app/settings/general')
    }

    switch (newPlatform) {
      case 'microsoft': {
        const token = sessionStorage.getItem('msTokensAccessToken')
        if (!token) {
          history.push('/app/settings/general')
          return
        }
        await fetchMicrosoftUsers(msAuth, token, setPlatformUsers, handleError)
        break
      }
      case 'slack': {
        const token = localStorage.getItem('slackTokensBotToken')
        if (!token) {
          history.push('/app/settings/general')
          return
        }
        await fetchSlackUsers(slackAuth, token, setPlatformUsers, handleError)
        break
      }
      case 'google': {
        const token = googleAuth.getAccessToken()
        if (!token) {
          logger.error('no token')
          history.push('/app/settings/general')
          return
        }
        await fetchGoogleUsers(googleAuth, token, setPlatformUsers, handleError)
        break
      }
      case 'email': {
        break
      }
      default:
        logger.error(`Unsupported platform: ${newPlatform}`)
        history.push('/app/settings/general')
    }
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const handleHideInactive = (e: RadioChangeEvent) => {
    setHideInactive(e.target.value === 'yes')
  }

  const handleMarkUnmappedAsDeleted = (e: RadioChangeEvent) => {
    setMarkUnmappedAsDeleted(e.target.value === 'yes')
  }

  const handleSelectChange = (value: string, vtUserId: string): void => {
    const matchedUser = platformUsers.find(user => user.id === value)
    const vtUser = usersForMigration.find(user => user.id === vtUserId)
    const newMapping: IUserMapping | null = value === 'unmapped' ? null : matchedUser ? {
      id: value,
      displayName: isMicrosoftUser(matchedUser) ? matchedUser.displayName : isSlackUser(matchedUser) ? matchedUser.profile.real_name_normalized : matchedUser.name,
      // For microsoft users, email is not returned from the graph api, so we need to use userPrincipalName
      // If we want the email to be returned, we need to use "user.read.all" scopes which are not used at the moment in project
      // Cases when userPrincipalName is NOT returned(in that user will be mapped but will not have app access-there will be modal for that and they will need to contact support):
      // Guest users, Custom Domain Config, On-Premises Active Directory Sync, Users with Multiple Email Addresses, Legacy Migrations, B2B Users
      mail: isMicrosoftUser(matchedUser) ? matchedUser.mail || matchedUser.userPrincipalName : isSlackUser(matchedUser) ? matchedUser.profile.email : matchedUser.email,
      isOwner: isSlackUser(matchedUser) ? matchedUser.is_primary_owner : undefined,
      isAdmin: vtUser?.isAdmin,
      initialUser: vtUser?.initialUser,
    } : null

    // Check for duplicate IDs in newMappings
    if (newMapping !== null) {
      const duplicateEntry = Object.entries(userMappings).find(([key, mapping]) => 
        key !== vtUserId && mapping !== null && mapping.id === newMapping.id
      )
      
      if (duplicateEntry) {
        const [duplicateVtUserId, duplicateMapping] = duplicateEntry
        const duplicateVtUser = usersForMigration.find(user => user.id === duplicateVtUserId)
        console.warn('⚠️ Duplicate ID detected:', {
          id: newMapping.id,
          currentUser: vtUser?.email,
          duplicateUser: duplicateVtUser?.email,
          mapping: duplicateMapping,
        })
        
        // Set duplicate mapping error state
        setDuplicateMappings(prev => ({
          ...prev,
          [vtUserId]: {
            duplicateUserEmail: duplicateVtUser?.email || 'Unknown user',
            displayName: newMapping.displayName || 'Unknown',
          },
        }))

        return
      } else {
        // Clear duplicate mapping error for this user if it was previously set
        setDuplicateMappings(prev => {
          const newState = { ...prev }
          delete newState[vtUserId]
          return newState
        })
      }
    } else {
      // Clear duplicate mapping error when unmapping
      setDuplicateMappings(prev => {
        const newState = { ...prev }
        delete newState[vtUserId]
        return newState
      })
    }

    const newMappings: UserMappings = {
      ...userMappings,
      [vtUserId]: newMapping,
    }
    
    const unmappedUsers = Object.values(newMappings).filter(mapping => mapping === null)
    setUnmappedUsers(unmappedUsers.length)
    setUserMappings(newMappings)
    saveUserMappingsToStorage(newMappings)
  }

  const handleSelectSearch = (value: string) => {
    setSearchPlatformUsersText(value)
    // If the search term is not empty and we have many users, set filtering state to true
    if (value && platformUsers.length > 100) {
      setIsFilteringPlatformUsers(true)
    }
  }

  const handleCancel = () => {
    history.push('/app/settings/general')
  }

  const showConfirmationDialog = () => {
    modal.confirm({
      title: formatMessage({ id: 'company.platformChanged.confirmTitle' }),
      width: 650,
      content: (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text>
              {formatMessage({ id: 'company.platformChanged.confirmDescription.info' })}
            </Text>
          </div>
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            <Text>
              {formatMessage({ id: 'company.platformChanged.confirmDescription.activateDeletedUsers' })}
            </Text>
          </div>
          
          {markUnmappedAsDeleted && unmappedUsers > 0 && (
            <Alert
              message={formatMessage({ id: 'company.platformChanged.confirmDescription.unmappedUsers.deleted' }, { unmappedUsers, newPlatform: capitalize(newPlatform) })}
              type="warning"
              style={{ marginBottom: 16 }}
            />
          )}
          {!markUnmappedAsDeleted && unmappedUsers > 0 && (
            <Alert
              message={formatMessage({ id: 'company.platformChanged.confirmDescription.unmappedUsers.unmatched' }, { unmappedUsers, newPlatform: capitalize(newPlatform) })}
              type="warning"
              style={{ marginBottom: 16 }}
            />
          )}
          {isAboveMaxUsers && (
            <Alert
              message={formatMessage({ id: 'company.platformChanged.confirmDescription.aboveMaxUsers' })}
              type="warning"
              style={{ marginBottom: 16 }}
            />
          )}
          {newPlatform === 'email' && (
            <Alert
              message={formatMessage({ id: 'company.platformChanged.confirmDescription.email.title' })}
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>{formatMessage({ id: 'company.platformChanged.confirmDescription.email.line1' })}</li>
                  <li>{formatMessage({ id: 'company.platformChanged.confirmDescription.email.line2' })}</li>
                  <li>{formatMessage({ id: 'company.platformChanged.confirmDescription.email.line3' })}</li>
                </ul>
              }
              type="warning"
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      ),
      onOk: () => startMigrationProcess(),
      okText: formatMessage({ id: 'app.proceed' }),
      cancelText: formatMessage({ id: 'app.cancel' }),
    })
  }

  const startMigrationProcess = () => {
    (async () => {
      setIsSavingMapping(true)
      try {
        // For email platform, make sure all users are included in userMappings
        let mappingsToUse = { ...userMappings }
        
        if (newPlatform === 'email') {
          // Create a copy to avoid directly modifying state
          mappingsToUse = { ...userMappings }
          
          // Ensure all users from usersForMigration are in the mappings
          usersForMigration.forEach(user => {
            if (!mappingsToUse[user.id]) {
              // Add any missing users with their default email
              mappingsToUse[user.id] = {
                id: user.id,
                displayName: user.name,
                mail: debouncedEmailInputs[user.id] || user.email, // Use any updated email
                isAdmin: user.isAdmin,
                initialUser: user.initialUser,
              }
            }
          })
        }

        // Check for duplicate IDs and emails in mappingsToUse
        const idSet = new Set<string>()
        const emailSet = new Set<string>()
        const duplicates: { ids: string[]; emails: string[] } = { ids: [], emails: [] }

        Object.entries(mappingsToUse).forEach(([, mapping]) => {
          if (mapping) {
            // Check for duplicate IDs
            if (mapping.id) {
              if (idSet.has(mapping.id)) {
                duplicates.ids.push(mapping.id)
              } else {
                idSet.add(mapping.id)
              }
            }
            // Check for duplicate emails
            if (mapping.mail) {
              if (emailSet.has(mapping.mail)) {
                duplicates.emails.push(mapping.mail)
              } else {
                emailSet.add(mapping.mail)
              }
            }
          }
        })

        // Additional check, in case the UI duplicate-user check doesn’t catch it.
        if (duplicates.ids.length > 0 || duplicates.emails.length > 0) {
          // If duplicates found, show error and stop
          const errorMessages: string[] = []
          if (duplicates.ids.length > 0) {
            errorMessages.push(formatMessage({ id: 'company.platformChanged.error.duplicateIds' }, { ids: [...new Set(duplicates.ids)].join(', ') }))
          }
          if (duplicates.emails.length > 0) {
            errorMessages.push(formatMessage({ id: 'company.platformChanged.error.duplicateEmails' }, { emails: [...new Set(duplicates.emails)].join(', ') }))
          }
          
          notification.error({
            message: formatMessage({ id: 'company.platformChanged.error.invalidUserMappings' }),
            description: errorMessages.join('\n'),
            duration: 0,
          })
          setIsSavingMapping(false)
          return
        }
        
        const event: ICompanyPlatformChangedEventBody = {
          eventType: 'COMPANY_PLATFORM_CHANGED',
          eventGroup: 'COMPANY',
          newPlatform: newPlatform,
          oldPlatform: oldPlatform,
          userMappings: mappingsToUse,
          organizationId: getOrganizationId(newPlatform, state), 
          slackOrgTeamId: newPlatform === 'slack' ? slackOrgTeamId : undefined,
          slackBotId: newPlatform === 'slack' ? slackBotId : undefined,
          deleteUnmapped: markUnmappedAsDeleted,
          token: getTokensForPlatform(newPlatform),
        }
        
        console.log('event', event)
        // Calculate and log payload size
        const payloadSize = calculatePayloadSize(event)
        console.log(`Payload size: ${payloadSize.kilobytes} KB`)

        // Track analytics event
        await track('COMPANY_PLATFORM_CHANGED', {
          newPlatform,
          oldPlatform,
          userCount: Object.keys(mappingsToUse).length,
          unmappedUserCount: Object.values(mappingsToUse).filter(mapping => mapping === null).length,
          organizationId: event.organizationId,
          deleteUnmapped: markUnmappedAsDeleted,
        })
        
        // Add warning if payload is approaching the limit
        if (parseFloat(payloadSize.kilobytes) > 400) {
          // Show warning
          modal.warning({
            title: 'Large Payload Warning',
            content: `The request payload is ${payloadSize.kilobytes} KB, which is approaching the 400KB limit. This may cause issues with the migration.`,
            okText: 'Continue Anyway',
            cancelText: 'Cancel',
            onOk: async () => {
              // Proceed with request
              const response = await Api.post('/core/event', event)
              notification.open({
                key: response.correlationId,
                message: formatMessage({ id: 'company.platformChanged' }),
                icon: (<LoadingOutlined />),
                duration: 0,
              })
              setActionNotifications([...actionNotifications, response.correlationId])
              setCorrelationId(response.correlationId as string)
              localStorage.removeItem('userMappings')
            },
          })
          return
        }
        
        // Normal flow if payload is not too large
        const response = await Api.post('/core/event', {
          ...event,
        })
        
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'company.platformChanged' }),
          icon: (<LoadingOutlined />),
          duration: 0,
        })
        setActionNotifications([...actionNotifications, response.correlationId])
        setCorrelationId(response.correlationId as string)
        localStorage.removeItem('userMappings')
      } catch (error) {
        logger.error('Error creating event:', error)
        notification.error({
          message: formatMessage({ id: 'error.generic' }),
          description: error.response.data.message,
          duration: 0,
        })
        setIsSavingMapping(false)
      }
    })()
  }

  const handleSave = () => {
    if (Object.values(invalidEmails).some(invalid => invalid)) {
      notification.error({
        message: formatMessage({ id: 'error.fixInvalidEmails' }),
        duration: 3,
      })
      return
    }
    showConfirmationDialog()
  }

  const filteredData = usersForMigration.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchText?.toLowerCase())
    const matchesStatus = hideInactive ? user.status === 'ACTIVE' : true
    return matchesSearch && matchesStatus
  })

  const columns: ColumnsType<IGetUserListForMigration> = [
    {
      title: <IntlMessages id="company.platformChanged.vacationTrackerUser" />,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <IntlMessages id="app.email" />,
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: <IntlMessages id="app.status" />,
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: newPlatform === 'email' ? <IntlMessages id="app.email" /> : <IntlMessages id={`company.platformChanged.${newPlatform}Account`} />,
      dataIndex: `${newPlatform}Account`,
      key: `${newPlatform}Account`,
      render: (text, record) => {
        if (newPlatform === 'email') {
          let invalidEmail: string | undefined = undefined
          if (invalidEmails[record.id]) {
            invalidEmail = formatMessage({ id: 'error.invalidEmailFormat' })
          } else if (duplicateEmails[record.id]) {
            invalidEmail = formatMessage({ id: 'company.platformChanged.error.duplicateEmail' }) 
          }
          const invalidEmailStatus = invalidEmails[record.id] || duplicateEmails[record.id] ? 'error' : undefined

          return (
            <>
              <Tooltip 
                title={invalidEmail}
                color={invalidEmailStatus}
              >
                <Input
                  defaultValue={record.email}
                  status={invalidEmailStatus}
                  onChange={(e) => {
                    const email = e.target.value
                    // Update email input to trigger debounce
                    setEmailInputs(prev => ({
                      ...prev,
                      [record.id]: email,
                    }))
                  }}
                />
              </Tooltip>
              {duplicateEmails[record.id] && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  {formatMessage({ id: 'company.platformChanged.error.duplicateMapping' })}
                </div>
              )}
            </>
          )
        }
        return (
          <div>
            <Tooltip
              title={
                duplicateMappings[record.id]
                  ? formatMessage(
                    { id: 'company.platformChanged.duplicateMapping.description' },
                    {
                      platform: capitalize(newPlatform),
                      displayName: duplicateMappings[record.id].displayName,
                      duplicateUserEmail: duplicateMappings[record.id].duplicateUserEmail,
                    }
                  )
                  : undefined
              }
              color={duplicateMappings[record.id] ? 'error' : undefined}
            >
              <Select
                style={{ width: '100%' }}
                value={userMappings[record.id]?.displayName || 'unmapped'}
                options={userOptions}
                status={duplicateMappings[record.id] ? 'error' : undefined}
                onChange={(value) => handleSelectChange(value, record.id)}
                filterOption={false}
                showSearch
                onDropdownVisibleChange={(open) => {
                  if (open) {
                    setActiveSelectId(record.id)
                    setSearchPlatformUsersText('')
                    // Reset filtering state when opening dropdown
                    setIsFilteringPlatformUsers(false)
                  } else {
                    setActiveSelectId(null)
                  }
                }}
                onSearch={handleSelectSearch}
                key={record.id}
                loading={usersForMigrationLoading || (activeSelectId === record.id && isFilteringPlatformUsers)}
                notFoundContent={
                  isFilteringPlatformUsers ? 
                    <div style={{ textAlign: 'center', padding: '10px' }}>
                      <LoadingOutlined style={{ marginRight: 8 }} />
                      Loading results...
                    </div> : undefined
                }
              />
            </Tooltip>
            {duplicateMappings[record.id] && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {formatMessage({ id: 'company.platformChanged.error.duplicateMapping' })}
              </div>
            )}
          </div>
        )
      },
    },
  ]

  const validateEmailFormat = (email: string, userId: string): boolean => {
    if (!email) {
      setInvalidEmails(prev => ({ ...prev, [userId]: true }))
      // Also clear any duplicate flags for this user
      setDuplicateEmails(prev => {
        const newState = { ...prev }
        delete newState[userId]
        return newState
      })
      return false
    }
    
    const isValid = validEmailWithRegex(email)
    setInvalidEmails(prev => {
      if (isValid) {
        const newState = { ...prev }
        delete newState[userId]
        return newState
      }
      return { ...prev, [userId]: true }
    })
    
    // When format changes, reset duplicate status to be rechecked
    if (isValid) {
      setDuplicateEmails(prev => {
        const newState = { ...prev }
        delete newState[userId]
        return newState
      })
    }
    
    return isValid
  }

  // Function to check for duplicate emails
  const validateEmailsForDuplicates = () => {
    // Clear previous duplicate flags
    const newDuplicateEmails: Record<string, boolean> = {}
    
    // Create a map to track emails and their user IDs
    const emailsMap: Record<string, string[]> = {}
    
    // Process all email inputs and current user emails
    filteredData.forEach(user => {
      const email = debouncedEmailInputs[user.id] || user.email
      if (!email) return
      
      const normalizedEmail = email?.toLowerCase().trim()
      if (!emailsMap[normalizedEmail]) {
        emailsMap[normalizedEmail] = []
      }
      emailsMap[normalizedEmail].push(user.id)
    })
    
    // Mark users with duplicate emails
    Object.entries(emailsMap).forEach(([email, userIds]) => {
      if (userIds.length > 1) {
        // Mark all users with this email as having a duplicate
        userIds.forEach(userId => {
          newDuplicateEmails[userId] = true
        })
      }
    })
    
    // Update the duplicate emails state
    setDuplicateEmails(newDuplicateEmails)
  }

  // Function to calculate payload size in bytes
  function calculatePayloadSize(payload) {
    const jsonString = JSON.stringify(payload)
    
    // Method 1: String length in characters (approximate)
    const stringLength = jsonString.length
    
    // Method 2: More accurate byte length calculation
    const blob = new Blob([jsonString])
    const byteLength = blob.size
    
    return {
      kilobytes: (byteLength / 1024).toFixed(2),
      megabytes: (byteLength / (1024 * 1024)).toFixed(2),
      bytes: byteLength,
    }
  }

  if (isLoading || isMappingUsers) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <LoadingOutlined style={{ fontSize: 40, marginBottom: 24 }} />
            <Typography.Title level={4}>{loadingMessage}</Typography.Title>
            <Progress percent={loadingProgress} status="active" />
            {isFetchingUsers && (
              <Typography.Text type="secondary">
                {formatMessage({ id: 'company.platformChanged.loadingUsersFromCompanyDatabase' })}
              </Typography.Text>
            )}
            {isFetchingPlatformUsers && (
              <Typography.Text type="secondary">
                {formatMessage({ id: 'company.platformChanged.loadingUsersFromPlatform' }, { platform: capitalize(newPlatform) })}
              </Typography.Text>
            )}
            {isMappingUsers && (
              <Typography.Text type="secondary">
                {formatMessage({ id: 'company.platformChanged.matchingUsersBetweenSystems' })}
              </Typography.Text>
            )}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={3}>
        {newPlatform === oldPlatform 
          ? <IntlMessages id="company.platformChangedTenant.title" values={{ newPlatform: capitalize(newPlatform) }} /> 
          : <IntlMessages id="company.platformChanged.title" values={{ oldPlatform: capitalize(oldPlatform), newPlatform: capitalize(newPlatform) }} />
        }
      </Title>
      <Text><IntlMessages id="company.platformChanged.description" values={{ newPlatform: capitalize(newPlatform) }} /></Text>
      <Row style={{ margin: '20px 0' }} justify="end">
        <Space>
          <Text><IntlMessages id="company.platformChanged.hideInactive" /></Text>
          <Radio.Group onChange={handleHideInactive} value={hideInactive ? 'yes' : 'no'} disabled={isAboveMaxUsers}>
            <Radio.Button value="yes"><IntlMessages id="app.yes" /></Radio.Button>
            <Radio.Button value="no"><IntlMessages id="app.no" /></Radio.Button>
          </Radio.Group>
          <Input placeholder={formatMessage({ id: 'users.search' })} value={searchText} onChange={handleSearch} style={{ width: 200 }} />
        </Space>
      </Row>
      <Table columns={columns} dataSource={filteredData} pagination={{ position: ['bottomRight'] }} rowKey="id" />
      <Row style={{ marginTop: '20px' }}>
        <Col flex={1}>
          <Space>
            <Text><IntlMessages id="company.platformChanged.markUnmappedAsDeleted" /></Text>
            <Radio.Group onChange={handleMarkUnmappedAsDeleted} value={markUnmappedAsDeleted ? 'yes' : 'no'}>
              <Radio.Button value="yes"><IntlMessages id="app.yes" /></Radio.Button>
              <Radio.Button value="no"><IntlMessages id="app.no" /></Radio.Button>
            </Radio.Group>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button onClick={handleCancel}><IntlMessages id="app.cancel" /></Button>
            <Button type="primary" onClick={handleSave} loading={isSavingMapping} disabled={Object.values(invalidEmails).some(invalid => invalid)}>
              <IntlMessages id="app.save" />
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal
        title={formatMessage({ id: 'company.platformChanged.migrationSuccessful' })}
        open={isLogoutModalVisible}
        closable={false}
        footer={null}
      >
        <p>{formatMessage({ id: 'company.platformChanged.logoutDescription' }, { countdown })}</p>
        <Button onClick={() => void handleLogout()}>{formatMessage({ id: 'app.logoutNow' })}</Button>
      </Modal>
    </div>
  )
}

export default MigrationPage
